import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RentalCostReport } from '../components/vehicles/RentalCostReport';
import {
  Car,
  Users,
  FileBadge,
  KeySquare,
  Wrench,
  RefreshCw,
  Download,
  Search,
  Loader2,
  ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Araç Raporları (Vehicle Reports) - Super Admin only.
 *
 * Backend: GET /api/vehicles/reports/summary
 * Returns: { drivers[], vergunning[], rentals[], substitutes[], service[], totals }
 *
 * Provides 5 tabbed tables, a global search filter, refresh, and CSV export
 * for the currently visible tab. Şub 2026.
 */

const TABS = [
  { key: 'drivers', label: 'Sürücü Atanmış', icon: Users, color: 'text-blue-600 dark:text-blue-400' },
  { key: 'vergunning', label: 'Vergunning', icon: FileBadge, color: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'rentals', label: 'Kiralık', icon: KeySquare, color: 'text-amber-600 dark:text-amber-400' },
  { key: 'substitutes', label: 'İkame Araçlar', icon: ArrowRightLeft, color: 'text-purple-600 dark:text-purple-400' },
  { key: 'service', label: 'Serviste / Askıda', icon: Wrench, color: 'text-rose-600 dark:text-rose-400' },
];

function formatDate(dt) {
  if (!dt) return '-';
  try {
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return String(dt);
    return d.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return String(dt);
  }
}

function toCSV(rows, headers) {
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const head = headers.map((h) => escape(h.label)).join(';');
  const body = rows
    .map((r) => headers.map((h) => escape(typeof h.value === 'function' ? h.value(r) : r[h.value])).join(';'))
    .join('\n');
  return `\uFEFF${head}\n${body}`;
}

function downloadCSV(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function VehicleReports() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('drivers');

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await api.get('/vehicles/reports/summary');
      if (res.data?.success) setData(res.data.data);
      else toast.error('Rapor yüklenemedi');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rapor yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const q = search.trim().toLowerCase();
  const filter = (rows, extraFields = []) => {
    if (!q) return rows;
    return rows.filter((r) => {
      const fields = [
        r.license_plate,
        r.brand,
        r.model,
        r.firm_name,
        r.driver_name,
        r.loaner_plate,
        r.reason,
        r.status_reason,
        r.ownership_type,
        ...extraFields.map((f) => (typeof f === 'function' ? f(r) : r[f])),
      ];
      return fields.some((v) => v && String(v).toLowerCase().includes(q));
    });
  };

  const drivers = useMemo(() => filter(data?.drivers || []), [data, q]);
  const vergunning = useMemo(
    () => filter(data?.vergunning || [], [(r) => (r.codes || []).map((c) => `${c.code} ${c.firm_name}`).join(' ')]),
    [data, q],
  );
  const rentals = useMemo(() => filter(data?.rentals || []), [data, q]);
  const substitutes = useMemo(() => filter(data?.substitutes || []), [data, q]);
  const service = useMemo(() => filter(data?.service || []), [data, q]);

  const totals = data?.totals || {};

  const exportCurrent = () => {
    let rows = [];
    let headers = [];
    let filename = 'arac-raporu.csv';
    switch (tab) {
      case 'drivers':
        rows = drivers;
        headers = [
          { label: 'Plaka', value: 'license_plate' },
          { label: 'Marka', value: 'brand' },
          { label: 'Model', value: 'model' },
          { label: 'Firma', value: 'firm_name' },
          { label: 'Sürücü', value: 'driver_name' },
          { label: 'Atama Tarihi', value: (r) => formatDate(r.assigned_at) },
        ];
        filename = 'arac-raporu-suruculer.csv';
        break;
      case 'vergunning':
        rows = vergunning.flatMap((r) =>
          (r.codes || []).map((c) => ({
            license_plate: r.license_plate,
            firm_name: r.firm_name,
            code: c.code,
            type: c.type,
            code_firm: c.firm_name,
          })),
        );
        headers = [
          { label: 'Plaka', value: 'license_plate' },
          { label: 'Araç Firması', value: 'firm_name' },
          { label: 'Vergunning Kodu', value: 'code' },
          { label: 'Tip (O/K)', value: 'type' },
          { label: 'Vergunning Firma', value: 'code_firm' },
        ];
        filename = 'arac-raporu-vergunning.csv';
        break;
      case 'rentals':
        rows = rentals;
        headers = [
          { label: 'Plaka', value: 'license_plate' },
          { label: 'Marka', value: 'brand' },
          { label: 'Model', value: 'model' },
          { label: 'Firma', value: 'firm_name' },
          { label: 'Mülkiyet', value: 'ownership_type' },
        ];
        filename = 'arac-raporu-kiralik.csv';
        break;
      case 'substitutes':
        rows = substitutes;
        headers = [
          { label: 'Asıl Araç ID', value: 'original_vehicle_id' },
          { label: 'İkame Plaka', value: 'loaner_plate' },
          { label: 'Marka', value: 'loaner_brand' },
          { label: 'Model', value: 'loaner_model' },
          { label: 'Kaynak', value: 'loaner_source' },
          { label: 'Sebep', value: 'reason' },
          { label: 'Başlangıç', value: (r) => formatDate(r.start_date) },
          { label: 'Bitiş', value: (r) => formatDate(r.end_date) },
          { label: 'Notlar', value: 'notes' },
        ];
        filename = 'arac-raporu-ikame.csv';
        break;
      case 'service':
        rows = service;
        headers = [
          { label: 'Plaka', value: 'license_plate' },
          { label: 'Marka', value: 'brand' },
          { label: 'Model', value: 'model' },
          { label: 'Firma', value: 'firm_name' },
          { label: 'Askı Sebebi', value: 'status_reason' },
          { label: 'Tarih', value: (r) => formatDate(r.since) },
        ];
        filename = 'arac-raporu-serviste.csv';
        break;
      default:
        return;
    }
    if (!rows.length) {
      toast.info('Dışa aktarılacak satır yok');
      return;
    }
    downloadCSV(filename, toCSV(rows, headers));
    toast.success(`${rows.length} satır CSV olarak indirildi`);
  };

  const plateLink = (id, plate) => (
    <Link
      to={`/dashboard/vehicles/${id}`}
      className="font-mono font-semibold text-primary hover:underline"
      data-testid={`vr-plate-link-${plate}`}
    >
      {plate}
    </Link>
  );

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="vehicle-reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Car className="w-7 h-7 text-primary" />
            Araç Raporu
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Filo genel görünümü — sürücü, vergunning, kiralık, ikame ve serviste olan araçlar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            data-testid="vr-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Yenile
          </Button>
          <Button size="sm" onClick={exportCurrent} data-testid="vr-export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV İndir
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Toplam Araç" value={totals.vehicles ?? 0} icon={Car} tone="text-primary" />
        <StatCard label="Sürücülü" value={totals.with_driver ?? 0} icon={Users} tone="text-blue-600 dark:text-blue-400" />
        <StatCard label="Kiralık" value={totals.rentals ?? 0} icon={KeySquare} tone="text-amber-600 dark:text-amber-400" />
        <StatCard label="İkame" value={totals.substitutes ?? 0} icon={ArrowRightLeft} tone="text-purple-600 dark:text-purple-400" />
        <StatCard label="Serviste" value={totals.service ?? 0} icon={Wrench} tone="text-rose-600 dark:text-rose-400" />
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Plaka, sürücü, firma veya kod ile ara..."
              className="pl-9"
              data-testid="vr-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full h-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="flex items-center gap-2 py-2"
                data-testid={`vr-tab-${t.key}`}
              >
                <Icon className={`w-4 h-4 ${t.color}`} />
                <span className="hidden md:inline">{t.label}</span>
                <span className="md:hidden">{t.label.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Drivers */}
        <TabsContent value="drivers">
          <ReportCard title="Sürücü Atanmış Araçlar" count={drivers.length} loading={loading}>
            <ReportTable
              testid="vr-drivers-table"
              empty="Aktif atama bulunamadı"
              rows={drivers}
              columns={[
                { label: 'Plaka', render: (r) => plateLink(r.vehicle_id, r.license_plate) },
                { label: 'Marka/Model', render: (r) => `${r.brand || ''} ${r.model || ''}`.trim() || '-' },
                { label: 'Firma', render: (r) => r.firm_name || '-' },
                { label: 'Sürücü', render: (r) => <span className="font-medium">{r.driver_name || '-'}</span> },
                { label: 'Atama Tarihi', render: (r) => formatDate(r.assigned_at) },
              ]}
            />
          </ReportCard>
        </TabsContent>

        {/* Vergunning */}
        <TabsContent value="vergunning">
          <ReportCard title="Vergunning Dağılımı" count={vergunning.length} loading={loading}>
            <ReportTable
              testid="vr-vergunning-table"
              empty="Vergunning kaydı olan araç yok"
              rows={vergunning}
              columns={[
                { label: 'Plaka', render: (r) => plateLink(r.vehicle_id, r.license_plate) },
                { label: 'Araç Firması', render: (r) => r.firm_name || '-' },
                {
                  label: 'Vergunning Kodları',
                  render: (r) => (
                    <div className="flex flex-wrap gap-1.5">
                      {(r.codes || []).map((c, i) => {
                        const isOriginal = c.type === 'ORIGINAL';
                        return (
                          <Link
                            key={`${c.code}-${i}`}
                            to={`/dashboard/vehicles/${r.vehicle_id}`}
                            className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-mono font-semibold transition hover:brightness-95 ${
                              isOriginal
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300'
                                : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-300'
                            }`}
                            title={c.firm_name ? `${c.code} — ${isOriginal ? 'Orijinal' : 'Kopya'} (${c.firm_name}) — Araca git` : `${c.code} — ${isOriginal ? 'Orijinal' : 'Kopya'} — Araca git`}
                            data-testid={`vr-verg-code-${r.license_plate}-${c.code}`}
                          >
                            {c.code}
                            <span className={`text-[9px] px-1 rounded font-bold ${isOriginal ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
                              {isOriginal ? 'O' : 'K'}
                            </span>
                          </Link>
                        );
                      })}
                      {!(r.codes || []).length && <span className="text-muted-foreground">-</span>}
                    </div>
                  ),
                },
              ]}
            />
          </ReportCard>
        </TabsContent>

        {/* Rentals */}
        <TabsContent value="rentals" className="space-y-6">
          <RentalCostReport />
        </TabsContent>

        {/* Substitutes */}
        <TabsContent value="substitutes">
          <ReportCard title="Aktif İkame Araçlar" count={substitutes.length} loading={loading}>
            <ReportTable
              testid="vr-subs-table"
              empty="Aktif ikame araç yok"
              rows={substitutes}
              columns={[
                {
                  label: 'Asıl Araç',
                  render: (r) => (
                    <Link
                      to={`/dashboard/vehicles/${r.original_vehicle_id}`}
                      className="text-primary hover:underline text-sm"
                      data-testid={`vr-sub-orig-${r.original_vehicle_id}`}
                    >
                      Detaya git
                    </Link>
                  ),
                },
                { label: 'İkame Plaka', render: (r) => <span className="font-mono font-semibold">{r.loaner_plate}</span> },
                {
                  label: 'Marka/Model',
                  render: (r) => `${r.loaner_brand || ''} ${r.loaner_model || ''}`.trim() || '-',
                },
                { label: 'Kaynak', render: (r) => r.loaner_source || '-' },
                { label: 'Sebep', render: (r) => r.reason || '-' },
                { label: 'Başlangıç', render: (r) => formatDate(r.start_date) },
              ]}
            />
          </ReportCard>
        </TabsContent>

        {/* Service / Suspended */}
        <TabsContent value="service">
          <ReportCard title="Serviste / Askıya Alınmış" count={service.length} loading={loading}>
            <ReportTable
              testid="vr-service-table"
              empty="Askıda araç yok"
              rows={service}
              columns={[
                { label: 'Plaka', render: (r) => plateLink(r.vehicle_id, r.license_plate) },
                { label: 'Marka/Model', render: (r) => `${r.brand || ''} ${r.model || ''}`.trim() || '-' },
                { label: 'Firma', render: (r) => r.firm_name || '-' },
                {
                  label: 'Sebep',
                  render: (r) => (
                    <span className="text-sm">{r.status_reason || <span className="text-muted-foreground">-</span>}</span>
                  ),
                },
                { label: 'Güncelleme', render: (r) => formatDate(r.since) },
              ]}
            />
          </ReportCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-bold leading-none" data-testid={`vr-stat-${label}`}>
            {value}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportCard({ title, count, loading, children }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="secondary">{count}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor...
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function ReportTable({ rows, columns, empty, testid }) {
  if (!rows.length) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c.label}>{c.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.vehicle_id || r.id || r.loaner_plate || i}>
              {columns.map((c) => (
                <TableCell key={c.label}>{c.render(r)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
